import { SubmitHandler } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { getAPIKeys, updateAPIKey } from 'api/apiKeys';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { apiQueryKeys } from 'queries/api-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface EditAPIKeyFormValues {
    name: string;
}

const EditAPIKeyDrawer = () => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const history = useHistory();
    const { apiKeyId } = useParams<{ apiKeyId: string }>();
    const params = { limit: 99 };
    const { data: apiKey } = useSuspenseQuery({
        queryKey: apiQueryKeys.apiKeysFiltered(accountId, params),
        queryFn: () => getAPIKeys(accountId, params),
        select: response => {
            return (
                response.data.filter(apiKey => {
                    return apiKeyId === apiKey.id.toString();
                })[0] ?? undefined
            );
        },
    });

    const methods = Form.useGivebutterForm<EditAPIKeyFormValues>({
        defaultValues: {
            name: apiKey?.name ?? '',
        },
    });
    const { handleSubmit, control, path, resetFormButton } = methods;

    const { mutate: submit } = useMutation({
        mutationFn: (values: EditAPIKeyFormValues) =>
            updateAPIKey(accountId, apiKey?.id ?? 0, {
                name: values.name,
            }),
        onSuccess: () => {
            toast.success('Updated API key');
            queryClient.invalidateQueries({ queryKey: apiQueryKeys.apiKeys(accountId) });
            history.push(`/accounts/${accountId}/settings/integrations/api`);
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data.message ?? 'Failed to update API key');
            resetFormButton();
        },
    });

    return (
        <Form.GivebutterFormProvider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<EditAPIKeyFormValues>)}
            >
                <Drawer.Header>
                    <Drawer.Title>Edit API key</Drawer.Title>
                </Drawer.Header>
                <Drawer.Body>
                    <Form.Container>
                        <Form.Label name={path('name')}>API key name</Form.Label>
                        <Form.TextInputRoot control={control} name="name">
                            <Form.TextInputForm placeholder="E.g. Zapier" />
                        </Form.TextInputRoot>
                        <Form.Error name={path('name')} />
                    </Form.Container>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant="transparent">Cancel</Button>
                        </Drawer.Cancel>

                        <Form.SubmitButton>Update</Form.SubmitButton>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.GivebutterFormProvider>
    );
};

export { EditAPIKeyDrawer };
